import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { projectsApi } from "@services/api";
import type { BillingSummary } from "@services/api/projects";
import { billingTemplatesApi, BillingTemplate } from "@services/api/billingTemplates";
import { Project, ProjectBillingStep } from "types";
import { navigateTo } from "context/navigation.service";
import Icon1 from "@assets/icons/Icon";
import DataGrid from "@components/ui/tables/DataGrid";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import { searchProjectsFull } from "@services/algolia";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { orgMembersToUsers, MemberAssignmentItem } from "@components/ui/blocks/MemberAssignmentBlock";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import BillingCommentsModal from "@components/modals/BillingCommentsModal";
import { PennylaneCredentialsCard } from "@components/ui/billing/PennylaneCredentialsCard";
import { formatEuros } from "@helpers/billingHelpers";

function StepCell({ step, onOpenPdf, onResend, resending, daysUntilEnd, prevSentAt }: {
  step: ProjectBillingStep | null;
  onOpenPdf?: (title: string) => void;
  onResend?: () => void;
  resending?: boolean;
  daysUntilEnd?: number | null;
  prevSentAt?: string | null;
}) {
  if (!step) return <span className="text-h5 text-neutral-300">—</span>;

  const hasPdf = !!onOpenPdf && !!(step.invoice_id || (step.invoice_pdf_url && step.invoice_pdf_url.startsWith("http")));
  const sentDate = step.sent_at ? new Date(step.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "";

  const isLate = (() => {
    if (!step.sent_at || step.status === "paid") return false;
    const delay = step.payment_delay_days ?? 15;
    return (new Date().getTime() - new Date(step.sent_at).getTime()) / (1000 * 60 * 60 * 24) > delay;
  })();

  const isProjectDelay = step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days;

  // Step gap delay
  const isStepGap = step.step_gap_delay_days && ["pending", "draft", "finalized"].includes(step.status) && prevSentAt && (() => {
    const daysSincePrev = (Date.now() - new Date(prevSentAt).getTime()) / (1000 * 60 * 60 * 24);
    return daysSincePrev > step.step_gap_delay_days!;
  })();

  const wrap = (content: React.ReactNode, className: string) =>
    hasPdf ? (
      <span
        className={`inline-block px-2 py-0.5 rounded text-h5 ${className} cursor-pointer`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenPdf?.(step.label);
        }}>
        {content}
      </span>
    ) : (
      <span className={`inline-block px-2 py-0.5 rounded text-h5 ${className}`}>{content}</span>
    );

  // Step gap retard (applies to pending/draft/finalized) — invoice not
  // sent yet, fall behind the previous step. Yellow per the suivi-salariés
  // convention: red is reserved for payment overdue / errors.
  if (isStepGap && ["pending", "draft", "finalized"].includes(step.status)) {
    const daysLate = Math.floor((Date.now() - new Date(prevSentAt!).getTime()) / (1000 * 60 * 60 * 24)) - step.step_gap_delay_days!;
    return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-100 text-amber-700">
      Retard étape · +{daysLate}j
    </span>;
  }

  switch (step.status) {
    case "paid":
      return wrap("Payée", "bg-emerald-100 text-emerald-700 hover:bg-emerald-200");
    case "sent": {
      // Surface only the manual relance timestamp here — the scheduler's
      // conductor alerts are not customer-facing and would mislabel a
      // Sunday cron run as a Sunday relance otherwise.
      const alertDate = step.last_relance_sent_at
        ? new Date(step.last_relance_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
        : null;
      return (
        <span className="inline-flex items-center gap-1" style={{ whiteSpace: "nowrap" }}>
          {isLate
            ? wrap(`En retard · ${sentDate}`, "bg-red-100 text-red-700 hover:bg-red-200")
            : wrap(`Envoyée · ${sentDate}`, "bg-blue-100 text-blue-700 hover:bg-blue-200")}
          {onResend && (
            <button
              className={`flex-shrink-0 p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 ${resending ? "animate-spin" : ""}`}
              title={alertDate ? `Relance demandée le ${alertDate}` : "Demander une relance au comptable"}
              disabled={resending}
              onClick={(e) => { e.stopPropagation(); onResend(); }}
            >
              <Icon1 name="sync" size={13} />
            </button>
          )}
          {alertDate && (
            <span className="inline-block px-1.5 py-0.5 rounded text-h6 bg-orange-50 text-orange-600" title={`Relance demandée le ${alertDate}`}>
              ↻ {alertDate}
            </span>
          )}
        </span>
      );
    }
    case "finalized":
      return wrap("Finalisée", "bg-blue-50 text-blue-600 hover:bg-blue-100");
    case "draft":
      return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-50 text-amber-600">Brouillon</span>;
    case "creating":
      return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-50 text-amber-500 animate-pulse">Création...</span>;
    case "error":
      return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-red-50 text-red-500">Erreur</span>;
    case "pending":
      if (isProjectDelay) {
        return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-100 text-amber-700">
          Retard · J-{daysUntilEnd}
        </span>;
      }
      return <span className="text-h5 text-neutral-300">—</span>;
    default:
      return <span className="text-h5 text-neutral-300">—</span>;
  }
}

const PAGE_SIZE = 10;

export default function FacturationScreen() {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const [billingTemplates, setBillingTemplates] = useState<BillingTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("en_cours");
  // Lazy-fetch state: two-phase (retards first, then the rest), each
  // phase paginated independently. `fetchedIds` dedups across phases —
  // a project that appears in the retards page must not re-appear in
  // the rest page even though the second query has no exclusion filter.
  const [fetchPhase, setFetchPhase] = useState<"retards" | "rest" | "done">("retards");
  const [phaseOffset, setPhaseOffset] = useState(0);
  const fetchingRef = useRef(false);
  const fetchedIdsRef = useRef<Set<string>>(new Set());
  // Billing-thread chat — modal target + optimistic per-project unread overrides.
  // Once the user opens a thread we zero out the badge locally so it disappears
  // immediately without waiting on a refetch of the project list.
  const [commentsModal, setCommentsModal] = useState<{ id: string; name: string } | null>(null);
  const [unreadOverrides, setUnreadOverrides] = useState<Record<string, number>>({});

  // Role-based access: admin/owner always see all, + users in billing_full_access_user_ids
  const billingFullAccessIds = orgData?.config?.billing_full_access_user_ids || [];
  const hasFullAccess = isOwnerOrAdmin(userData) || (userData?.id ? billingFullAccessIds.includes(userData.id) : false);
  // Conducteur filter dropdown + "Conducteur" column: shown to anyone with
  // full billing access (admin/owner or billing_full_access_user_ids). These
  // users oversee the whole book, so filtering by conducteur is useful even
  // when they are themselves a conducteur (e.g. a lead who also manages all
  // billing). Plain conducteurs without full access are already excluded by
  // hasFullAccess and scoped to their own projects server-side.
  const canFilterByConductor = hasFullAccess;
  // "Demander une relance" doesn't actually resend the invoice — it just
  // emails the configured comptables (billing_cc_user_ids) that the bill
  // needs a follow-up. Every user who can reach this page is allowed to
  // trigger that alert, conducteurs included. Guests + dessinateurs are
  // already blocked at the route level (navigation/index.tsx), and the
  // backend enforces "not a guest" + "same org" on the endpoint itself.
  const canResend = true;
  const [resendingKey, setResendingKey] = useState<string | null>(null);
  // Bulk archive selection. Reset on tab switch so the action label
  // (Archiver / Désarchiver) always matches the visible rows.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkArchiving, setBulkArchiving] = useState(false);

  const handleResend = useCallback(async (projectId: string, stepIndex: number) => {
    const key = `${projectId}-${stepIndex}`;
    setResendingKey(key);
    try {
      const updated = await projectsApi.resendBillingInvoice(projectId, stepIndex);
      setProjects((prev) => prev.map((p) => p.id === projectId ? updated : p));
      showNotification({ type: "success", message: "Demande de relance envoyée au comptable" });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showNotification({ type: "error", message: typeof detail === "string" ? detail : "Erreur lors de la demande de relance" });
    } finally {
      setResendingKey(null);
    }
  }, []);

  // Conductor filter (admin view only)
  const conductors = orgMembers.filter(
    (m) => m.role === "conductor" && m.status !== "inactive",
  );
  const [filterConductorId, setFilterConductorId] = useState<string>("");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Close filter on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node) &&
          filterBtnRef.current && !filterBtnRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const [pdfModal, setPdfModal] = useState<{ url: string; title: string } | null>(null);
  // Always fetch the PDF through the backend proxy, which re-resolves a
  // fresh URL from Pennylane. The stored `invoice_pdf_url` is a one-shot
  // link that Pennylane invalidates whenever the invoice is re-fetched;
  // sent steps get theirs refreshed by the sync cron, but paid steps are
  // skipped by sync so their stored URL is always dead (HTTP 400).
  const openPdf = useCallback(async (projectId: string, stepIndex: number | null | undefined, title: string) => {
    if (stepIndex == null) return;
    try {
      const url = await projectsApi.getBillingPdfUrl(projectId, stepIndex);
      setPdfModal({ url, title });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showNotification({ type: "error", message: typeof detail === "string" ? detail : "Impossible d'ouvrir la facture" });
    }
  }, []);
  const closePdf = useCallback(() => {
    setPdfModal((cur) => {
      if (cur?.url?.startsWith("blob:")) URL.revokeObjectURL(cur.url);
      return null;
    });
  }, []);

  // Fetch billing templates and auto-select all of them
  useEffect(() => {
    if (!userData?.org_id) return;
    billingTemplatesApi.list(userData.org_id).then((data) => {
      setBillingTemplates(data);
      setSelectedTemplateIds(data.map((t) => t.id));
    }).catch(() => {});
  }, [userData?.org_id]);

  // Determine which conductor to filter by.
  //
  // - Admins / billing_full_access_user_ids: optional dropdown filter.
  // - Other non-admins: no explicit conductor filter — let the backend's
  //   "restrict_to_team_for_user" scope the list to every project they're
  //   on (as conductor, commercial, SAV, dessinateur, etc. — not only
  //   those where they happen to be the lead conductor).
  const effectiveConductorId = hasFullAccess
    ? filterConductorId || undefined
    : undefined;

  const selectedTemplateIdParam = selectedTemplateIds.join(",");

  // Fetch a single batch of projects for the given phase. The retards
  // phase narrows down server-side via ``with_billing_retards`` so
  // those rows always reach the user first.
  const fetchBatch = useCallback(
    async (mode: "retards" | "rest", off: number): Promise<Project[]> => {
      if (!selectedTemplateIdParam || !userData?.org_id) return [];
      const params: any = {
        org_id: userData.org_id,
        status: "in_progress",
        billing_template_id: selectedTemplateIdParam,
        conductor_id: effectiveConductorId,
        limit: PAGE_SIZE,
        offset: off,
      };
      if (mode === "retards") params.with_billing_retards = true;
      return await projectsApi.list(params);
    },
    [selectedTemplateIdParam, userData?.org_id, effectiveConductorId],
  );

  const fetchProjects = useCallback(async () => {
    if (!selectedTemplateIdParam || !userData?.org_id) {
      setProjects([]);
      setFetchPhase("done");
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    // Deliberately NOT clearing `projects` here: emptying the list collapses
    // the scroller to zero height, which drops the user back to the top. The
    // first batch below replaces the rows in one go instead.
    setFetchPhase("retards");
    setPhaseOffset(0);
    fetchedIdsRef.current = new Set();
    try {
      const data = await fetchBatch("retards", 0);
      data.forEach((p) => fetchedIdsRef.current.add(p.id));
      setProjects(data);
      if (data.length < PAGE_SIZE) {
        // Retards exhausted — pull the first rest batch right away so
        // the user always sees a usable list, not just N<10 retards.
        const restData = await fetchBatch("rest", 0);
        const restFiltered = restData.filter((p) => !fetchedIdsRef.current.has(p.id));
        restFiltered.forEach((p) => fetchedIdsRef.current.add(p.id));
        setProjects((prev) => [...prev, ...restFiltered]);
        if (restData.length < PAGE_SIZE) {
          setFetchPhase("done");
        } else {
          setFetchPhase("rest");
          setPhaseOffset(restData.length);
        }
      } else {
        setPhaseOffset(data.length);
      }
    } catch {
      setProjects([]);
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [fetchBatch, selectedTemplateIdParam, userData?.org_id]);

  const loadMore = useCallback(async () => {
    if (fetchingRef.current) return;
    if (fetchPhase === "done") return;
    fetchingRef.current = true;
    try {
      // Accumulate all rows across fetches so we emit a single
      // setProjects call — multiple calls separated by awaits would
      // trigger separate React renders and cause a visible list jump.
      const newRows: Project[] = [];
      let nextPhase: "retards" | "rest" | "done" = fetchPhase;
      let nextOffset = phaseOffset;

      if (fetchPhase === "retards") {
        const data = await fetchBatch("retards", phaseOffset);
        const filtered = data.filter((p) => !fetchedIdsRef.current.has(p.id));
        filtered.forEach((p) => fetchedIdsRef.current.add(p.id));
        newRows.push(...filtered);

        if (data.length < PAGE_SIZE) {
          // Retards exhausted — also pull the first rest batch so
          // both land in a single render.
          const restData = await fetchBatch("rest", 0);
          const restFiltered = restData.filter((p) => !fetchedIdsRef.current.has(p.id));
          restFiltered.forEach((p) => fetchedIdsRef.current.add(p.id));
          newRows.push(...restFiltered);

          if (restData.length < PAGE_SIZE) {
            nextPhase = "done";
          } else {
            nextPhase = "rest";
            nextOffset = restData.length;
          }
        } else {
          nextOffset = phaseOffset + data.length;
        }
      } else {
        const data = await fetchBatch("rest", phaseOffset);
        const filtered = data.filter((p) => !fetchedIdsRef.current.has(p.id));
        filtered.forEach((p) => fetchedIdsRef.current.add(p.id));
        newRows.push(...filtered);

        if (data.length < PAGE_SIZE) {
          nextPhase = "done";
        } else {
          nextOffset = phaseOffset + data.length;
        }
      }

      // Single batched state update — React 18 merges these into one render.
      if (newRows.length > 0) {
        setProjects((prev) => [...prev, ...newRows]);
      }
      setFetchPhase(nextPhase);
      setPhaseOffset(nextOffset);
    } finally {
      fetchingRef.current = false;
    }
  }, [fetchPhase, phaseOffset, fetchBatch]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Header cards + tab counts come from a server-side aggregate over every
  // matching project: `projects` only holds the pages loaded so far.
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const summaryReqRef = useRef(0);
  const fetchSummary = useCallback(async () => {
    if (!selectedTemplateIdParam || !userData?.org_id) {
      setSummary(null);
      return;
    }
    const req = ++summaryReqRef.current;
    try {
      const data = await projectsApi.billingSummary({
        billing_template_id: selectedTemplateIdParam,
        status: "in_progress",
        conductor_id: effectiveConductorId,
      });
      if (req === summaryReqRef.current) setSummary(data);
    } catch {
      if (req === summaryReqRef.current) setSummary(null);
    }
  }, [selectedTemplateIdParam, userData?.org_id, effectiveConductorId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);


  // Sync with Pennylane, then re-run the lazy fetch from scratch so the
  // local state (phase/offset/dedup-set) stays consistent with what the
  // backend now exposes.
  const handleSync = useCallback(async () => {
    if (!selectedTemplateIdParam) return;
    setSyncing(true);
    try {
      await projectsApi.syncAllBilling(selectedTemplateIdParam);
      await Promise.all([fetchProjects(), fetchSummary()]);
    } catch {}
    setSyncing(false);
  }, [selectedTemplateIdParam, fetchProjects, fetchSummary]);

  const selectedTemplates = billingTemplates.filter((b) => selectedTemplateIds.includes(b.id));
  const hasDemarrage = selectedTemplates.some((t) => t.steps.some((s) => s.is_demarrage));

  // Anchor on the template with the MOST avancement steps so we never lose columns.
  // Templates with fewer steps simply leave their trailing columns empty per project.
  const anchorTemplate = useMemo(() => {
    if (selectedTemplates.length === 0) return null;
    return selectedTemplates.reduce((best, t) => {
      const count = t.steps.filter((s) => !s.is_demarrage).length;
      const bestCount = best ? best.steps.filter((s) => !s.is_demarrage).length : -1;
      return count > bestCount ? t : best;
    }, selectedTemplates[0]);
  }, [selectedTemplates]);

  const avancementTemplateSteps = anchorTemplate?.steps.filter((s) => !s.is_demarrage) ?? [];
  const maxAvancementCount = avancementTemplateSteps.length;

  // Compute reconciled column headers across all selected templates.
  // For each column index: if all templates that have a step at that index agree
  // on the abbreviation/label, use it. Otherwise use a generic "Facture N" with a
  // tooltip listing each template's specific label.
  const reconciledHeaders = useMemo(() => {
    if (selectedTemplates.length <= 1) return null;
    const allAvancement = selectedTemplates.map((t) => t.steps.filter((s) => !s.is_demarrage));
    const headers: { label: string; tooltip?: string }[] = [];
    for (let i = 0; i < maxAvancementCount; i++) {
      const labelsByTemplate = selectedTemplates.map((t, ti) => ({
        template: t.name,
        label: allAvancement[ti][i]?.abbreviation || allAvancement[ti][i]?.label || null,
      }));
      const present = labelsByTemplate.filter((x) => x.label);
      const unique = Array.from(new Set(present.map((x) => x.label!)));
      if (unique.length === 1) {
        headers.push({ label: unique[0] });
      } else {
        const tooltipLines = present.map((x) => `${x.template}: ${x.label}`).join(" / ");
        headers.push({ label: `Facture ${i + 1}`, tooltip: tooltipLines });
      }
    }
    return headers;
  }, [selectedTemplates, maxAvancementCount]);

  // Split projects into active and archived. A project lands in
  // "Archivés" if it is fully paid (auto) OR manually flagged via the
  // ``billing_archived_at`` timestamp set from the row action menu.
  const isFullyPaid = (p: Project) => {
    if (!p.billing?.steps.length) return false;
    return p.billing.steps.every((s) => s.status === "paid");
  };
  const isManuallyArchived = (p: Project) => !!p.billing?.billing_archived_at;
  const isArchived = (p: Project) => isFullyPaid(p) || isManuallyArchived(p);

  const activeProjects = projects.filter((p) => !isArchived(p));
  const archivedProjects = projects.filter(isArchived);
  const displayedProjects = activeTab === "en_cours" ? activeProjects : archivedProjects;

  // Auto-fill the visible tab: if the active tab has 0 rows but more
  // data is fetchable (different tab keeps the rows otherwise hidden,
  // e.g. user switched to "Archivés" before any archived project has
  // come down), keep pulling pages until something shows or we run out.
  useEffect(() => {
    if (displayedProjects.length > 0) return;
    if (isLoading) return;
    if (fetchPhase === "done") return;
    if (fetchingRef.current) return;
    loadMore();
  }, [displayedProjects.length, isLoading, fetchPhase, loadMore]);

  // Bulk archive/unarchive of selected rows. Direction is implied by the
  // active tab: "en_cours" archives, "archives" unarchives. Fully-paid
  // rows in the archives tab are skipped (backend rejects unarchive on
  // auto-archived projects — they have no ``billing_archived_at`` to clear).
  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    const archive = activeTab === "en_cours";
    const targets = projects.filter((p) => {
      if (!selectedIds.includes(p.id)) return false;
      if (archive) return !p.billing?.billing_archived_at;
      return !!p.billing?.billing_archived_at;
    });
    if (targets.length === 0) {
      showNotification({ type: "default", message: "Aucun projet à traiter dans la sélection." });
      return;
    }
    setBulkArchiving(true);
    try {
      const results = await Promise.allSettled(
        targets.map((p) => projectsApi.archiveBilling(p.id, archive)),
      );
      const succeeded: Project[] = [];
      let failed = 0;
      results.forEach((r) => {
        if (r.status === "fulfilled") succeeded.push(r.value);
        else failed++;
      });
      if (succeeded.length > 0) {
        setProjects((prev) =>
          prev.map((p) => succeeded.find((u) => u.id === p.id) || p),
        );
      }
      setSelectedIds([]);
      if (succeeded.length > 0) fetchSummary();
      if (failed === 0) {
        showNotification({
          type: "success",
          message: archive
            ? `${succeeded.length} facturation(s) archivée(s)`
            : `${succeeded.length} facturation(s) désarchivée(s)`,
        });
      } else {
        showNotification({
          type: "error",
          message: `${succeeded.length} traité(s), ${failed} échec(s).`,
        });
      }
    } finally {
      setBulkArchiving(false);
    }
  };

  // Stats (TTC = HT * 1.2)
  // Server-side `summary` covers every matching project; the local sums below
  // (loaded pages only) are just a fallback while it loads or if it fails.
  // Prefer pre-computed billing_flags (recomputed by backend on changes + daily cron).
  const TVA = 1.2;
  const localMontant = projects.reduce((sum, p) => sum + (p.billing?.montant_total || 0), 0);
  const localInvoiced = projects.reduce((sum, p) => {
    const flags = p.billing?.billing_flags;
    if (flags) return sum + flags.total_invoiced;
    return (
      sum +
      (p.billing?.steps || [])
        .filter((s) => ["finalized", "sent", "paid"].includes(s.status))
        .reduce((s2, st) => s2 + st.montant, 0)
    );
  }, 0);
  const localPaid = projects.reduce((sum, p) => {
    const flags = p.billing?.billing_flags;
    if (flags) return sum + flags.total_paid;
    return (
      sum +
      (p.billing?.steps || [])
        .filter((s) => s.status === "paid")
        .reduce((s2, st) => s2 + st.montant, 0)
    );
  }, 0);
  const localLate = projects.reduce((sum, p) => {
    const flags = p.billing?.billing_flags;
    return sum + (flags?.overdue_count || 0);
  }, 0);
  const projectCount = summary?.project_count ?? projects.length;
  const totalMontant = summary?.montant_total ?? localMontant;
  const totalInvoiced = summary?.total_invoiced ?? localInvoiced;
  const totalPaid = summary?.total_paid ?? localPaid;
  const lateCount = summary?.overdue_count ?? localLate;

  // Build DataGrid table
  const table = useMemo(() => {
    if (!anchorTemplate) return null;

    const getUnread = (projectId: string): number => {
      if (unreadOverrides[projectId] !== undefined) return unreadOverrides[projectId];
      const project = displayedProjects.find((p) => p.id === projectId);
      const uid = userData?.id;
      if (!project || !uid) return 0;
      return project.unseen_by_user?.[uid]?.billing_comments ?? 0;
    };

    const renderCommentsButton = (projectId: string, projectName: string) => {
      const unread = getUnread(projectId);
      const hasUnread = unread > 0;
      const project = displayedProjects.find((p) => p.id === projectId);
      const hasComments = (project?.billing?.comments?.length ?? 0) > 0;
      const iconColor = hasUnread ? "#ef4444" : hasComments ? "#f97316" : "#737373";
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCommentsModal({ id: projectId, name: projectName });
            setUnreadOverrides((prev) => ({ ...prev, [projectId]: 0 }));
          }}
          className={`relative inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-neutral-100 transition-colors ${
            hasUnread || hasComments ? "" : "opacity-60 hover:opacity-100"
          }`}
          title={hasUnread ? `${unread} nouveau${unread > 1 ? "x" : ""} commentaire${unread > 1 ? "s" : ""}` : "Commentaires"}
        >
          <Icon1 name="message" size={16} color={iconColor} />
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
              {unread > 9 ? "!" : unread}
            </span>
          )}
        </button>
      );
    };

    const conductorColumnVisible = canFilterByConductor && !filterConductorId;

    const columns: any[] = [
      {
        field: "name",
        headerName: "Projet",
        flex: 1,
        renderCell: ({ row }: any) => (
          <div className="flex flex-col cursor-pointer" onClick={(e) => { e.stopPropagation(); navigateTo({ route: `/project/${row.id}?tab=facturation` }); }}>
            <span className="text-h3 font-medium text-neutral-800 hover:underline">{row.name}</span>
            <span className="text-h4 text-neutral-400">{row.city}{row.num ? ` · ${row.num}` : ""}</span>
          </div>
        ),
      },
    ];

    if (conductorColumnVisible) {
      columns.push({
        field: "conductor",
        headerName: "Conducteur",
        width: 140,
        renderCell: ({ row }: any) => (
          <span className="text-h4 text-neutral-500 truncate">{row.conductor || "—"}</span>
        ),
      });
    }

    // Dedicated chat-icon column. Sits in its own fixed-width slot so every
    // row's icon lines up perfectly regardless of conductor / project name
    // length, and stays visible even when the conducteur column is hidden.
    columns.push({
      field: "comments",
      headerName: "",
      width: 48,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }: any) => (
        <div className="flex items-center justify-center w-full">
          {renderCommentsButton(row.id, row.name)}
        </div>
      ),
    });

    const colWidth = (label: string) => Math.max(120, Math.min(label.length * 8 + 30, 220));

    if (hasDemarrage) {
      columns.push({
        field: "demarrage",
        headerName: "Démarrage",
        width: colWidth("Démarrage"),
        renderCell: ({ row }: any) => <StepCell step={row.demarrage} onOpenPdf={(title) => openPdf(row.id, row._demarrageIdx, title)} daysUntilEnd={row._daysUntilEnd}
          onResend={canResend && row.demarrage?.status === "sent" && row._demarrageIdx != null ? () => handleResend(row.id, row._demarrageIdx) : undefined}
          resending={resendingKey === `${row.id}-${row._demarrageIdx}`} />,
      });
    }

    avancementTemplateSteps.forEach((s, i) => {
      const rh = reconciledHeaders?.[i];
      const headerLabel = rh ? rh.label : (s.abbreviation || s.label);
      const headerTooltip = rh?.tooltip;
      columns.push({
        field: `step_${i}`,
        headerName: headerLabel,
        headerTooltip,
        width: colWidth(headerLabel),
        renderCell: ({ row }: any) => <StepCell step={row[`step_${i}`]} onOpenPdf={(title) => openPdf(row.id, row[`_stepIdx_${i}`], title)} daysUntilEnd={row._daysUntilEnd} prevSentAt={row[`_prevSent_${i}`]}
          onResend={canResend && row[`step_${i}`]?.status === "sent" && row[`_stepIdx_${i}`] != null ? () => handleResend(row.id, row[`_stepIdx_${i}`]) : undefined}
          resending={resendingKey === `${row.id}-${row[`_stepIdx_${i}`]}`} />,
      });
    });

    columns.push({
      field: "total",
      headerName: "Total TTC",
      width: 100,
      renderCell: ({ row }: any) => (
        <span className="text-h3 font-medium text-neutral-700">{row.total} €</span>
      ),
    });

    const rows = displayedProjects.map((project) => {
      const billing = project.billing;
      if (!billing) return null;

      const demarrageSteps = billing.steps.filter((s) => s.is_demarrage);
      const avancementSteps = billing.steps.filter((s) => !s.is_demarrage);

      const conductorId = project.infos?.team?.conductor_id;
      const conductorMember = conductorId ? conductors.find((c) => c.id === conductorId) : null;

      // Days until project end (from cached planning_end_date)
      const endDateStr = billing.planning_end_date;
      const daysUntilEnd = endDateStr ? Math.round((new Date(endDateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

      const row: any = {
        id: project.id,
        name: project.infos?.general?.name || project.id,
        city: project.infos?.general?.city || "",
        num: project.infos?.general?.num || "",
        conductor: conductorMember?.infos?.display_name || "",
        total: formatEuros(billing.montant_total * TVA),
        _daysUntilEnd: daysUntilEnd,
      };

      if (hasDemarrage) {
        const firstDemarrageOrigIdx = billing.steps.findIndex((s) => s.is_demarrage);
        row.demarrage = demarrageSteps.length > 0 ? {
          ...demarrageSteps[0],
          montant: demarrageSteps.reduce((sum, s) => sum + s.montant, 0),
        } : null;
        row._demarrageIdx = firstDemarrageOrigIdx >= 0 ? firstDemarrageOrigIdx : null;
      }

      avancementSteps.forEach((step, i) => {
        row[`step_${i}`] = step;
        // Store original billing step index for resend
        const origIdx = billing.steps.findIndex((s) => s === step);
        row[`_stepIdx_${i}`] = origIdx >= 0 ? origIdx : null;
        // Find previous step's sent_at for step gap detection
        if (i > 0) {
          const prev = avancementSteps[i - 1];
          if (prev.sent_at && ["sent", "paid"].includes(prev.status)) {
            row[`_prevSent_${i}`] = prev.sent_at;
          }
        }
      });

      return row;
    }).filter(Boolean);

    return { columns, rows };
  }, [displayedProjects, anchorTemplate, hasDemarrage, avancementTemplateSteps, reconciledHeaders, openPdf, canResend, handleResend, resendingKey, canFilterByConductor, filterConductorId, unreadOverrides, userData?.id]);

  const tabs = [
    { value: "en_cours", label: `En cours (${summary?.active_count ?? activeProjects.length})` },
    { value: "archives", label: `Archivés (${summary?.archived_count ?? archivedProjects.length})` },
  ];

  return (
    <div className="h-screen flex flex-col overflow-auto">
      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col w-full max-w-1p2k">
          <div className="flex flex-col mb-4">
            <Text1>Suivi de facturation</Text1>
            <Text2>Vue d'ensemble de la facturation de tous les projets.</Text2>
          </div>

          <PennylaneCredentialsCard isAdmin={isOwnerOrAdmin(userData)} />

          {/* Toolbar: sync + conductor filter */}
          <div className="flex flex-row gap-2 flex-wrap items-center">
            <button
              onClick={handleSync}
              disabled={syncing || selectedTemplateIds.length === 0}
              className="flex items-center gap-1.5 text-h4 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
            >
              <Icon1 name="sync" size={14} />
              {syncing ? "Synchronisation..." : "Synchroniser"}
            </button>

            {/* Conductor filter — shown only to users who actually manage
                multiple conducteurs' books (admins + billing-full-access).
                Conducteurs themselves never see this filter. */}
            {canFilterByConductor && (
              <>
                <button
                  ref={filterBtnRef}
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`inline-flex items-center gap-1 px-3 py-2 border rounded-lg text-h4 ml-2 ${
                    filterConductorId
                      ? "border-neutral-800 text-neutral-800 bg-neutral-100"
                      : "border-neutral-300 text-neutral-500 hover:bg-neutral-50"
                  }`}
                >
                  <Icon1 name="expert" size={14} />
                  {filterConductorId
                    ? conductors.find((c) => c.id === filterConductorId)?.infos?.display_name || "Conducteur"
                    : "Conducteur"}
                  {filterConductorId && (
                    <span
                      className="ml-1 text-h5 text-neutral-400 hover:text-neutral-600"
                      onClick={(e) => { e.stopPropagation(); setFilterConductorId(""); }}
                    >
                      ✕
                    </span>
                  )}
                </button>
                {filterOpen && createPortal(
                  <div
                    ref={filterDropdownRef}
                    style={{
                      position: "fixed",
                      top: (filterBtnRef.current?.getBoundingClientRect().bottom || 0) + 4,
                      left: filterBtnRef.current?.getBoundingClientRect().left || 0,
                    }}
                    className="z-50 bg-white border rounded-lg shadow-lg p-2 min-w-200 max-h-300 overflow-auto"
                  >
                    <div
                      className={`px-3 py-1.5 rounded text-h4 cursor-pointer ${!filterConductorId ? "bg-neutral-100 font-medium" : "hover:bg-neutral-50"}`}
                      onClick={() => { setFilterConductorId(""); setFilterOpen(false); }}
                    >
                      Tous les conducteurs
                    </div>
                    {conductors.map((c) => (
                      <div
                        key={c.id}
                        className={`px-3 py-1.5 rounded text-h4 cursor-pointer ${filterConductorId === c.id ? "bg-neutral-100 font-medium" : "hover:bg-neutral-50"}`}
                        onClick={() => { setFilterConductorId(c.id); setFilterOpen(false); }}
                      >
                        {c.infos?.display_name || c.infos?.email || c.id}
                      </div>
                    ))}
                  </div>,
                  document.body,
                )}
              </>
            )}
          </div>

          {/* Non-admin info */}
          {!hasFullAccess && (
            <div className="text-h4 text-neutral-400 mt-2">
              Vous voyez uniquement les projets dont vous êtes conducteur de travaux.
            </div>
          )}
        </div>
      </div>

      {selectedTemplateIds.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="flex-shrink-0 px-4 lg:px-10 pb-4">
            <div className="flex flex-row gap-3 max-w-1p2k">
              {/* Same rule as the grid below: skeletons only before the first
                  rows land, so a refetch doesn't reflow everything above the
                  table and shift the scroll under the user. */}
              {isLoading && projects.length === 0 ? (
                <>
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex-1 bg-white rounded-lg border px-4 py-3">
                      <div className="h-3 w-16 bg-neutral-100 rounded animate-pulse mb-2" />
                      <div className="h-5 w-20 bg-neutral-100 rounded animate-pulse" />
                    </div>
                  ))}
                </>
              ) : (
                <>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Projets</div>
                <div className="text-h1 font-semibold text-neutral-800">{projectCount}</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Montant total TTC</div>
                <div className="text-h1 font-semibold text-neutral-800">{formatEuros(totalMontant * TVA)} €</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Facturé</div>
                <div className="text-h1 font-semibold text-emerald-600">{formatEuros(totalInvoiced * TVA)} €</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Encaissé</div>
                <div className="text-h1 font-semibold text-emerald-700">{formatEuros(totalPaid * TVA)} €</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Restant</div>
                <div className="text-h1 font-semibold text-amber-600">{formatEuros((totalMontant - totalInvoiced) * TVA)} €</div>
              </div>
              {lateCount > 0 && (
                <div className="flex-1 bg-white rounded-lg border border-red-200 px-4 py-3">
                  <div className="text-h4 text-neutral-400">En retard</div>
                  <div className="text-h1 font-semibold text-red-600">{lateCount}</div>
                </div>
              )}
                </>
              )}
            </div>
          </div>

          {/* DataGrid */}
          <div className="px-4 lg:px-10 pb-6 flex flex-col w-full">
            <div className="flex items-center gap-3 mb-2 min-h-7">
              {syncing && !isLoading && (
                <div className="flex items-center gap-2 text-h4 text-neutral-400">
                  <div className="w-3 h-3 border-2 border-neutral-300 border-t-neutral-500 rounded-full animate-spin" />
                  Synchronisation avec Pennylane...
                </div>
              )}
              {/* Refetch with rows already on screen: say so here instead of
                  replacing the table with a skeleton. */}
              {isLoading && projects.length > 0 && (
                <div className="flex items-center gap-2 text-h4 text-neutral-400">
                  <div className="w-3 h-3 border-2 border-neutral-300 border-t-neutral-500 rounded-full animate-spin" />
                  Mise à jour...
                </div>
              )}
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkArchive}
                  disabled={bulkArchiving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 text-h4 text-neutral-700 hover:bg-neutral-100 hover:border-neutral-500 hover:text-neutral-900 transition-colors disabled:opacity-50"
                >
                  <Icon1
                    name={activeTab === "archives" ? "restart" : "archive"}
                    size={14}
                    color="#525252"
                  />
                  {bulkArchiving
                    ? "Traitement..."
                    : activeTab === "archives"
                      ? `Désarchiver la sélection (${selectedIds.length})`
                      : `Archiver la sélection (${selectedIds.length})`}
                </button>
              )}
            </div>
            {/* Skeleton only on the FIRST load. Once rows are on screen we keep
                the grid mounted through refetches — swapping it for a skeleton
                unmounts the scroller and sends the user back to the top. */}
            {isLoading && projects.length === 0 ? (
              <div className="flex flex-col gap-2 max-w-1p5k">
                <div className="h-10 rounded-lg bg-neutral-100 animate-pulse" />
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-12 rounded-lg bg-neutral-50 animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
                ))}
              </div>
            ) : table ? (
              <div className="max-w-1p5k w-full" style={{ minHeight: 500 }}>
                <DataGrid
                  table={table}
                  hide_lines={true}
                  initial_sort_field={null}
                  initialPageSize={9999}
                  selectable={true}
                  handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={(tab) => { setActiveTab(tab); setSelectedIds([]); }}
                  onScrollNearBottom={loadMore}
                  onSearch={
                    orgData?.id && userData?.id
                      ? (q) => searchProjectsFull(q, orgData.id, {
                          userId: userData.id,
                          canViewAll: isOwnerOrAdmin(userData) || !!userData.config?.can_view_all_projects,
                        })
                      : undefined
                  }
                  onSearchSelect={(option) =>
                    navigateTo({ route: `/project/${option.value}?tab=facturation` })
                  }
                  storageKey="facturation"
                />
              </div>
            ) : (
              <div className="text-h3 text-neutral-400 py-12 text-center">
                Aucun projet avec ce modèle de facturation.
              </div>
            )}
          </div>
        </>
      )}

      {pdfModal && (
        <PDFViewerModal
          pdfUrl={pdfModal.url}
          title={`Facture — ${pdfModal.title}`}
          onClose={closePdf}
        />
      )}

      {commentsModal && (() => {
        const proj = projects.find((p) => p.id === commentsModal.id);
        return (
          <BillingCommentsModal
            projectId={commentsModal.id}
            projectName={commentsModal.name}
            initialComments={proj?.billing?.comments || []}
            onCommentsChange={(next) => {
              // Keep the local projects array in sync so the row's badge
              // and any subsequent open-modal start with the new comments.
              setProjects((prev) =>
                prev.map((p) =>
                  p.id === commentsModal.id && p.billing
                    ? { ...p, billing: { ...p.billing, comments: next } }
                    : p,
                ),
              );
            }}
            onClose={() => setCommentsModal(null)}
          />
        );
      })()}
    </div>
  );
}
